/*
 * Formularvorlagen (U01). Verweise auf Boardinhalte als Namen: „label:Druck“, „field:Wunschtermin“.
 * Der Server löst sie beim Anlegen auf und legt fehlende Labels/Felder an (im Dialog angekündigt).
 * Die Zielspalte wählt die Person beim Anlegen.
 */
import type { FieldType, LabelColor } from './types.js';
import type { FormDefinition } from './forms.js';

export interface FormTemplate {
  key: string;
  name: string;
  summary: string;
  needs: { labels: { name: string; color: LabelColor }[]; fields: { name: string; type: FieldType }[] };
  definition: Omit<FormDefinition, 'mapping'> & { mapping: Omit<FormDefinition['mapping'], 'columnId' | 'columnConfirmed'> };
}

const yes = (id: string) => ({ mode: 'all' as const, items: [{ q: id, op: 'eq' as const, value: true }] });
const is = (q: string, value: string) => ({ mode: 'all' as const, items: [{ q, op: 'eq' as const, value }] });
/** Beide Bedingungen (Zuordnungsregeln greifen nur einzeln – die erste passende zählt, darum Kombinationen zuerst) */
type Atoms = { mode: 'all'; items: { q: string; op: 'eq'; value: string | boolean }[] };
const both = (a: Atoms, b: Atoms) => ({ mode: 'all' as const, items: [...a.items, ...b.items] });
const add = (...names: string[]) => ({ mode: 'add' as const, ids: names.map((n) => `label:${n}`) });

const baseMapping = {
  placement: 'bottom' as const,
  description: { mode: 'summary' as const, questionIds: null },
  fields: [],
  start: null,
  due: null,
  labelIds: [],
  priority: 0,
  assigneeIds: [],
  coverQ: null,
  cardTemplateId: null,
  contactQ: 'email' as string | null,
};

const contact = [
  { kind: 'question' as const, id: 'name', type: 'text' as const, label: 'Dein Name', required: true, maxLength: 120 },
  { kind: 'question' as const, id: 'email', type: 'email' as const, label: 'E-Mail-Adresse für Rückfragen', required: true },
];

export const FORM_TEMPLATES: FormTemplate[] = [
  {
    key: 'anfrage',
    name: 'Allgemeine Anfrage',
    summary: 'Anliegen, Beschreibung und Kontakt – für jede Art von Anfrage',
    needs: { labels: [], fields: [] },
    definition: {
      schemaVersion: 1,
      title: 'Anfrage',
      intro: 'Schreib uns kurz, worum es geht. Wir melden uns so bald wie möglich.',
      pages: [
        {
          id: 'p1',
          title: 'Dein Anliegen',
          items: [
            { kind: 'question', id: 'betreff', type: 'text', label: 'Worum geht es?', required: true, maxLength: 150, placeholder: 'z. B. Frage zur Anmeldung' },
            { kind: 'question', id: 'text', type: 'longtext', label: 'Beschreibung', required: true, maxLength: 5000 },
            {
              kind: 'question',
              id: 'dringend',
              type: 'yesno',
              label: 'Ist es dringend?',
              help: 'Dringend heißt: Es muss in den nächsten zwei Tagen erledigt sein.',
            },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:betreff}}', due: { days: 7 } },
      routes: [{ id: 'r1', name: 'Dringend', when: yes('dringend'), priority: 3, dueDays: 2 }],
      confirmation: { text: 'Danke, deine Anfrage ist bei uns eingegangen.', expectation: 'Wir antworten in der Regel innerhalb einer Woche.' },
      tests: [{ id: 't1', name: 'Normale Anfrage', answers: { betreff: 'Frage zum Kurs', text: 'Wann beginnt der Kurs?', dringend: false, name: 'Alex Beispiel', email: 'alex@example.org' } }],
    },
  },
  {
    key: 'gestaltung',
    name: 'Gestaltungsauftrag',
    summary: 'Flyer, Plakat, Social Media – mit Auflage nur bei Druck und Motivdateien',
    needs: { labels: [{ name: 'Druck', color: 'orange' }, { name: 'Digital', color: 'blue' }], fields: [{ name: 'Wunschtermin', type: 'date' }] },
    definition: {
      schemaVersion: 1,
      title: 'Neuer Gestaltungsauftrag',
      intro: 'Beschreib, was gestaltet werden soll. Je genauer, desto schneller geht es.',
      pages: [
        {
          id: 'p1',
          title: 'Auftrag',
          items: [
            { kind: 'question', id: 'titel', type: 'text', label: 'Titel des Auftrags', required: true, maxLength: 120, placeholder: 'z. B. Flyer Sommerfest' },
            {
              kind: 'question',
              id: 'leistung',
              type: 'choice',
              label: 'Welche Leistung wird benötigt?',
              required: true,
              display: 'radio',
              options: [
                { id: 'druck', label: 'Druckprodukt' },
                { id: 'digital', label: 'Digital' },
                { id: 'beratung', label: 'Beratung' },
              ],
            },
            {
              kind: 'question',
              id: 'format',
              type: 'choice',
              label: 'Format',
              visibleIf: is('leistung', 'druck'),
              requiredIf: is('leistung', 'druck'),
              options: [
                { id: 'a4', label: 'A4' },
                { id: 'a5', label: 'A5' },
                { id: 'a3', label: 'A3 (Plakat)' },
                { id: 'din-lang', label: 'DIN lang' },
              ],
            },
            { kind: 'question', id: 'auflage', type: 'number', label: 'Auflage', unit: 'Stück', decimals: 0, min: 1, max: 100000, visibleIf: is('leistung', 'druck'), requiredIf: is('leistung', 'druck') },
            {
              kind: 'question',
              id: 'kanal',
              type: 'multichoice',
              label: 'Kanäle',
              visibleIf: is('leistung', 'digital'),
              requiredIf: is('leistung', 'digital'),
              options: [
                { id: 'web', label: 'Website' },
                { id: 'insta', label: 'Instagram' },
                { id: 'mail', label: 'Newsletter' },
              ],
            },
            { kind: 'question', id: 'beschreibung', type: 'longtext', label: 'Inhalt und Wünsche', required: true },
            { kind: 'question', id: 'termin', type: 'date', label: 'Wunschtermin', help: 'Bis wann brauchst du das Ergebnis? Das ist ein Wunsch, keine feste Zusage.' },
            { kind: 'question', id: 'dateien', type: 'file', label: 'Texte, Logos oder Bilder', maxFiles: 5, maxSizeMb: 10, accept: ['image', 'pdf', 'text'] },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:titel}}', fields: [{ fieldId: 'field:Wunschtermin', q: 'termin' }], coverQ: 'dateien' },
      routes: [
        { id: 'r1', name: 'Druck', when: is('leistung', 'druck'), labels: { mode: 'add', ids: ['label:Druck'] } },
        { id: 'r2', name: 'Digital', when: is('leistung', 'digital'), labels: { mode: 'add', ids: ['label:Digital'] } },
      ],
      confirmation: { text: 'Danke, dein Auftrag ist eingegangen.', expectation: 'Wir melden uns mit einer ersten Einschätzung.' },
      tests: [
        {
          id: 't1',
          name: 'Flyer',
          answers: { titel: 'Flyer Sommerfest', leistung: 'druck', format: 'a5', auflage: 500, beschreibung: 'Bunt und fröhlich', name: 'Alex Beispiel', email: 'alex@example.org' },
        },
      ],
    },
  },
  {
    key: 'veranstaltung',
    name: 'Veranstaltungsbedarf',
    summary: 'Druck, Technik mit mehreren Positionen und Summe, oder Sonstiges',
    needs: { labels: [{ name: 'Druck', color: 'orange' }, { name: 'Technik', color: 'sky' }], fields: [{ name: 'Gesamtbetrag', type: 'currency' }] },
    definition: {
      schemaVersion: 1,
      title: 'Bedarf für eine Veranstaltung',
      intro: 'Was braucht ihr für eure Veranstaltung? Je nach Bedarf fragen wir die passenden Angaben ab.',
      pages: [
        {
          id: 'p1',
          title: 'Veranstaltung',
          items: [
            { kind: 'question', id: 'veranstaltung', type: 'text', label: 'Veranstaltung', required: true, maxLength: 120 },
            { kind: 'question', id: 'datum', type: 'date', label: 'Datum der Veranstaltung', required: true },
            {
              kind: 'question',
              id: 'bedarf',
              type: 'choice',
              label: 'Bedarf',
              required: true,
              display: 'radio',
              options: [
                { id: 'druck', label: 'Druck' },
                { id: 'technik', label: 'Technik' },
                { id: 'sonst', label: 'Sonstiges' },
              ],
            },
          ],
        },
        {
          id: 'p2',
          title: 'Druck',
          visibleIf: is('bedarf', 'druck'),
          items: [
            {
              kind: 'question',
              id: 'format',
              type: 'choice',
              label: 'Format',
              required: true,
              options: [
                { id: 'a4', label: 'A4' },
                { id: 'a3', label: 'A3' },
                { id: 'banner', label: 'Banner' },
              ],
            },
            { kind: 'question', id: 'auflage', type: 'number', label: 'Auflage', unit: 'Stück', decimals: 0, min: 1, required: true },
            { kind: 'question', id: 'motiv', type: 'file', label: 'Motivdatei', required: true, maxFiles: 3, maxSizeMb: 10, accept: ['image', 'pdf'] },
          ],
        },
        {
          id: 'p3',
          title: 'Technik',
          visibleIf: is('bedarf', 'technik'),
          items: [
            {
              kind: 'section',
              id: 'positionen',
              title: 'Positionen',
              help: 'Eine Zeile je Gerät oder Leistung.',
              repeat: { min: 1, max: 20, itemLabel: 'Position' },
              items: [
                { kind: 'question', id: 'bez', type: 'text', label: 'Bezeichnung', required: true, maxLength: 120 },
                { kind: 'question', id: 'menge', type: 'number', label: 'Menge', decimals: 0, min: 1, required: true },
                { kind: 'question', id: 'preis', type: 'currency', label: 'Einzelpreis', min: 0 },
                {
                  kind: 'question',
                  id: 'zeile',
                  type: 'calc',
                  label: 'Zeilensumme',
                  decimals: 2,
                  unit: '€',
                  formula: { k: 'op', op: '*', a: { k: 'ref', q: 'menge' }, b: { k: 'ref', q: 'preis' } },
                },
              ],
            },
            { kind: 'question', id: 'summe', type: 'calc', label: 'Gesamtsumme', decimals: 2, unit: '€', formula: { k: 'sum', q: 'zeile' } },
          ],
        },
        {
          id: 'p4',
          title: 'Sonstiges',
          visibleIf: is('bedarf', 'sonst'),
          items: [{ kind: 'question', id: 'sonstiges', type: 'longtext', label: 'Was wird benötigt?', required: true }],
        },
        { id: 'p5', title: 'Kontakt', items: [...contact] },
      ],
      mapping: { ...baseMapping, title: '{{q:veranstaltung}} · Bedarf', start: null, fields: [{ fieldId: 'field:Gesamtbetrag', q: 'summe' }] },
      routes: [
        { id: 'r1', name: 'Druck', when: is('bedarf', 'druck'), labels: { mode: 'add', ids: ['label:Druck'] } },
        { id: 'r2', name: 'Technik', when: is('bedarf', 'technik'), labels: { mode: 'add', ids: ['label:Technik'] } },
      ],
      confirmation: { text: 'Danke, euer Bedarf ist eingegangen.', expectation: 'Wir melden uns, sobald alles geklärt ist.' },
      tests: [
        { id: 't1', name: 'Sonstiges', answers: { veranstaltung: 'Sommerfest', datum: '2030-07-01', bedarf: 'sonst', sonstiges: 'Bierbänke', name: 'Alex Beispiel', email: 'alex@example.org' } },
      ],
    },
  },
  {
    key: 'fehler',
    name: 'Fehlermeldung',
    summary: 'Was ist passiert, wie dringend, mit Bildschirmfoto',
    needs: { labels: [{ name: 'Fehler', color: 'red' }], fields: [] },
    definition: {
      schemaVersion: 1,
      title: 'Fehler melden',
      intro: 'Etwas funktioniert nicht? Beschreib kurz, was passiert ist.',
      pages: [
        {
          id: 'p1',
          title: 'Fehler',
          items: [
            { kind: 'question', id: 'was', type: 'text', label: 'Was funktioniert nicht?', required: true, maxLength: 150 },
            { kind: 'question', id: 'schritte', type: 'longtext', label: 'Was hast du gemacht, bevor der Fehler auftrat?' },
            {
              kind: 'question',
              id: 'schwere',
              type: 'choice',
              label: 'Wie stark behindert dich der Fehler?',
              required: true,
              display: 'radio',
              options: [
                { id: 'gering', label: 'Kaum – es gibt einen Umweg' },
                { id: 'mittel', label: 'Spürbar' },
                { id: 'stark', label: 'Ich kann nicht weiterarbeiten' },
              ],
            },
            { kind: 'question', id: 'foto', type: 'file', label: 'Bildschirmfoto', maxFiles: 3, maxSizeMb: 10, accept: ['image'] },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:was}}', labelIds: ['label:Fehler'], coverQ: 'foto' },
      routes: [{ id: 'r1', name: 'Blockiert die Arbeit', when: is('schwere', 'stark'), priority: 4, dueDays: 1 }],
      confirmation: { text: 'Danke für deine Meldung.', expectation: 'Wir kümmern uns darum.' },
      tests: [{ id: 't1', name: 'Kleiner Fehler', answers: { was: 'Knopf reagiert nicht', schwere: 'gering', name: 'Alex Beispiel', email: 'alex@example.org' } }],
    },
  },

  /* ---------- 0.77: Formulare, die mit Board-Vorlagen als Entwurf kommen ---------- */
  {
    key: 'itticket',
    name: 'IT-Ticket',
    summary: 'Hardware, Konto oder Software – Gerät, Beschreibung, Bildschirmfoto, „ich kann nicht arbeiten“',
    needs: {
      labels: [
        { name: 'Hardware', color: 'gray' },
        { name: 'Konto', color: 'blue' },
        { name: 'Software', color: 'green' },
        { name: 'Dringend', color: 'red' },
      ],
      fields: [],
    },
    definition: {
      schemaVersion: 1,
      title: 'IT-Problem melden',
      intro: 'Beschreib kurz, was nicht funktioniert. Wir melden uns, sobald jemand das Ticket übernimmt.',
      pages: [
        {
          id: 'p1',
          title: 'Dein Ticket',
          items: [
            {
              kind: 'question',
              id: 'art',
              type: 'choice',
              label: 'Worum geht es?',
              required: true,
              display: 'radio',
              options: [
                { id: 'hardware', label: 'Gerät (Laptop, Drucker, Telefon …)' },
                { id: 'konto', label: 'Konto, Passwort oder Zugang' },
                { id: 'software', label: 'Programm oder App' },
                { id: 'sonst', label: 'Etwas anderes' },
              ],
            },
            { kind: 'question', id: 'was', type: 'text', label: 'Was ist das Problem?', required: true, maxLength: 150, placeholder: 'z. B. Drucker im 2. OG druckt nicht' },
            { kind: 'question', id: 'geraet', type: 'text', label: 'Welches Gerät bzw. welcher Raum?', maxLength: 120, placeholder: 'z. B. Laptop Empfang, Raum 2.14' },
            { kind: 'question', id: 'details', type: 'longtext', label: 'Was ist passiert? Gibt es eine Fehlermeldung?', maxLength: 5000 },
            {
              kind: 'question',
              id: 'folge',
              type: 'choice',
              label: 'Wie stark behindert dich das?',
              required: true,
              display: 'radio',
              options: [
                { id: 'kaum', label: 'Kaum – es gibt einen Umweg' },
                { id: 'spuerbar', label: 'Spürbar' },
                { id: 'stopp', label: 'Ich kann nicht arbeiten' },
              ],
            },
            { kind: 'question', id: 'foto', type: 'file', label: 'Bildschirmfoto oder Foto', maxFiles: 3, maxSizeMb: 10, accept: ['image'] },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:was}}' },
      routes: [
        // Erst „kann nicht arbeiten“ je Art (dringend + Art), dann die Art allein
        { id: 'r1', name: 'Gerät – kann nicht arbeiten', when: both(is('art', 'hardware'), is('folge', 'stopp')), labels: add('Hardware', 'Dringend'), priority: 4, dueDays: 1 },
        { id: 'r2', name: 'Konto – kann nicht arbeiten', when: both(is('art', 'konto'), is('folge', 'stopp')), labels: add('Konto', 'Dringend'), priority: 4, dueDays: 1 },
        { id: 'r3', name: 'Programm – kann nicht arbeiten', when: both(is('art', 'software'), is('folge', 'stopp')), labels: add('Software', 'Dringend'), priority: 4, dueDays: 1 },
        { id: 'r4', name: 'Kann nicht arbeiten', when: is('folge', 'stopp'), labels: add('Dringend'), priority: 4, dueDays: 1 },
        { id: 'r5', name: 'Gerät', when: is('art', 'hardware'), labels: add('Hardware') },
        { id: 'r6', name: 'Konto', when: is('art', 'konto'), labels: add('Konto') },
        { id: 'r7', name: 'Programm', when: is('art', 'software'), labels: add('Software') },
      ],
      confirmation: { text: 'Danke, dein Ticket ist eingegangen.', expectation: 'Wir melden uns, sobald jemand es übernimmt.' },
      tests: [
        { id: 't1', name: 'Drucker', answers: { art: 'hardware', was: 'Drucker druckt nicht', geraet: 'Raum 2.14', folge: 'spuerbar', name: 'Alex Beispiel', email: 'alex@example.org' } },
        { id: 't2', name: 'Gesperrt', answers: { art: 'konto', was: 'Konto gesperrt', folge: 'stopp', name: 'Alex Beispiel', email: 'alex@example.org' } },
      ],
    },
  },
  {
    key: 'rueckmeldung',
    name: 'Fehler oder Wunsch',
    summary: 'Fehler melden oder etwas vorschlagen – mit Version und Bildschirmfoto',
    needs: { labels: [{ name: 'Fehler', color: 'red' }, { name: 'Wunsch', color: 'sky' }], fields: [{ name: 'Version', type: 'text' }] },
    definition: {
      schemaVersion: 1,
      title: 'Rückmeldung geben',
      intro: 'Etwas funktioniert nicht oder du hast eine Idee? Wir lesen jede Rückmeldung.',
      pages: [
        {
          id: 'p1',
          title: 'Rückmeldung',
          items: [
            {
              kind: 'question',
              id: 'art',
              type: 'choice',
              label: 'Was möchtest du uns sagen?',
              required: true,
              display: 'radio',
              options: [
                { id: 'fehler', label: 'Etwas funktioniert nicht' },
                { id: 'wunsch', label: 'Ich habe einen Wunsch oder eine Idee' },
              ],
            },
            { kind: 'question', id: 'was', type: 'text', label: 'In einem Satz', required: true, maxLength: 150 },
            { kind: 'question', id: 'schritte', type: 'longtext', label: 'Was hast du gemacht, bevor der Fehler auftrat?', visibleIf: is('art', 'fehler') },
            {
              kind: 'question',
              id: 'schwere',
              type: 'choice',
              label: 'Wie stark behindert dich der Fehler?',
              display: 'radio',
              visibleIf: is('art', 'fehler'),
              requiredIf: is('art', 'fehler'),
              options: [
                { id: 'gering', label: 'Kaum – es gibt einen Umweg' },
                { id: 'mittel', label: 'Spürbar' },
                { id: 'stark', label: 'Ich kann nicht weiterarbeiten' },
              ],
            },
            { kind: 'question', id: 'nutzen', type: 'longtext', label: 'Wobei würde dir das helfen?', visibleIf: is('art', 'wunsch') },
            { kind: 'question', id: 'version', type: 'text', label: 'Version oder Seite (falls bekannt)', maxLength: 60 },
            { kind: 'question', id: 'foto', type: 'file', label: 'Bildschirmfoto', maxFiles: 3, maxSizeMb: 10, accept: ['image'] },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:was}}', fields: [{ fieldId: 'field:Version', q: 'version' }] },
      routes: [
        { id: 'r1', name: 'Fehler, blockiert die Arbeit', when: both(is('art', 'fehler'), is('schwere', 'stark')), labels: add('Fehler'), priority: 4, dueDays: 1 },
        { id: 'r2', name: 'Fehler', when: is('art', 'fehler'), labels: add('Fehler') },
        { id: 'r3', name: 'Wunsch', when: is('art', 'wunsch'), labels: add('Wunsch') },
      ],
      confirmation: { text: 'Danke für deine Rückmeldung.', expectation: 'Wir bewerten sie und melden uns bei Rückfragen.' },
      tests: [
        { id: 't1', name: 'Fehler', answers: { art: 'fehler', was: 'Speichern geht nicht', schwere: 'stark', version: '2.1', name: 'Alex Beispiel', email: 'alex@example.org' } },
        { id: 't2', name: 'Wunsch', answers: { art: 'wunsch', was: 'Dunkler Modus', nutzen: 'Abends angenehmer', name: 'Alex Beispiel', email: 'alex@example.org' } },
      ],
    },
  },
  {
    key: 'mitgliedsantrag',
    name: 'Mitgliedsantrag',
    summary: 'Mitgliedsart, Anschrift, Einverständnis – ohne Bankdaten',
    needs: { labels: [{ name: 'Familie', color: 'green' }, { name: 'Jugend', color: 'yellow' }, { name: 'Förderer', color: 'purple' }], fields: [] },
    definition: {
      schemaVersion: 1,
      title: 'Mitglied werden',
      intro: 'Schön, dass du dabei sein möchtest! Beitrag und Zahlungsweise klären wir nach der Aufnahme mit dir.',
      pages: [
        {
          id: 'p1',
          title: 'Mitgliedschaft',
          items: [
            {
              kind: 'question',
              id: 'art',
              type: 'choice',
              label: 'Art der Mitgliedschaft',
              required: true,
              display: 'radio',
              options: [
                { id: 'einzel', label: 'Einzelmitglied' },
                { id: 'familie', label: 'Familie' },
                { id: 'jugend', label: 'Jugendliche (unter 18)' },
                { id: 'foerderer', label: 'Fördermitglied' },
              ],
            },
            { kind: 'question', id: 'name', type: 'text', label: 'Vor- und Nachname', required: true, maxLength: 120 },
            { kind: 'question', id: 'strasse', type: 'text', label: 'Straße und Hausnummer', required: true, maxLength: 120 },
            { kind: 'question', id: 'ort', type: 'text', label: 'PLZ und Ort', required: true, maxLength: 80 },
            { kind: 'question', id: 'email', type: 'email', label: 'E-Mail-Adresse', required: true },
            { kind: 'question', id: 'telefon', type: 'phone', label: 'Telefon (freiwillig)' },
            { kind: 'question', id: 'familie', type: 'longtext', label: 'Weitere Familienmitglieder (Vornamen)', maxLength: 500, visibleIf: is('art', 'familie') },
            {
              kind: 'question',
              id: 'erziehung',
              type: 'text',
              label: 'Name einer erziehungsberechtigten Person',
              maxLength: 120,
              visibleIf: is('art', 'jugend'),
              requiredIf: is('art', 'jugend'),
              help: 'Bei Jugendlichen unter 18 muss eine erziehungsberechtigte Person zustimmen.',
            },
            { kind: 'question', id: 'interesse', type: 'longtext', label: 'Wobei möchtest du mitmachen? (freiwillig)', maxLength: 1000 },
            { kind: 'question', id: 'satzung', type: 'confirm', label: 'Ich erkenne die Satzung des Vereins an.', required: true },
            { kind: 'question', id: 'datenschutz', type: 'confirm', label: 'Ich bin einverstanden, dass meine Angaben für die Mitgliederverwaltung gespeichert werden.', required: true },
          ],
        },
      ],
      mapping: { ...baseMapping, title: 'Neues Mitglied: {{q:name}}' },
      routes: [
        { id: 'r1', name: 'Familie', when: is('art', 'familie'), labels: { mode: 'add', ids: ['label:Familie'] } },
        { id: 'r2', name: 'Jugend', when: is('art', 'jugend'), labels: { mode: 'add', ids: ['label:Jugend'] } },
        { id: 'r3', name: 'Förderer', when: is('art', 'foerderer'), labels: { mode: 'add', ids: ['label:Förderer'] } },
      ],
      confirmation: { text: 'Danke, dein Antrag ist eingegangen.', expectation: 'Wir melden uns, sobald wir über die Aufnahme entschieden haben.' },
      tests: [
        {
          id: 't1',
          name: 'Familie',
          answers: { art: 'familie', name: 'Alex Beispiel', strasse: 'Hauptstraße 1', ort: '12345 Musterstadt', email: 'alex@example.org', familie: 'Kim, Jo', satzung: true, datenschutz: true },
        },
      ],
    },
  },
  {
    key: 'beitrag',
    name: 'Beitrag einreichen (Call for Papers)',
    summary: 'Vortrag oder Workshop mit Kurzbeschreibung, Dauer und Technikbedarf',
    needs: { labels: [{ name: 'Vortrag', color: 'blue' }, { name: 'Workshop', color: 'green' }], fields: [{ name: 'Referent*in', type: 'text' }] },
    definition: {
      schemaVersion: 1,
      title: 'Beitrag einreichen',
      intro: 'Du möchtest auf unserer Tagung einen Vortrag halten oder einen Workshop anbieten? Wir freuen uns auf deinen Vorschlag.',
      pages: [
        {
          id: 'p1',
          title: 'Dein Beitrag',
          items: [
            { kind: 'question', id: 'titel', type: 'text', label: 'Titel des Beitrags', required: true, maxLength: 150 },
            {
              kind: 'question',
              id: 'format',
              type: 'choice',
              label: 'Format',
              required: true,
              display: 'radio',
              options: [
                { id: 'vortrag', label: 'Vortrag' },
                { id: 'workshop', label: 'Workshop' },
                { id: 'sonst', label: 'Anderes Format' },
              ],
            },
            { kind: 'question', id: 'kurz', type: 'longtext', label: 'Kurzbeschreibung (für das Programm)', required: true, maxLength: 1500 },
            {
              kind: 'question',
              id: 'dauer',
              type: 'choice',
              label: 'Gewünschte Dauer',
              required: true,
              options: [
                { id: '30', label: '30 Minuten' },
                { id: '45', label: '45 Minuten' },
                { id: '60', label: '60 Minuten' },
                { id: '90', label: '90 Minuten' },
              ],
            },
            {
              kind: 'question',
              id: 'technik',
              type: 'multichoice',
              label: 'Was brauchst du an Technik?',
              required: true,
              options: [
                { id: 'beamer', label: 'Beamer / Bildschirm' },
                { id: 'mikro', label: 'Mikrofon' },
                { id: 'internet', label: 'Internet' },
                { id: 'keine', label: 'Nichts davon' },
              ],
            },
            { kind: 'question', id: 'technik_sonst', type: 'text', label: 'Sonstiger Bedarf (Raum, Material …)', maxLength: 300 },
            { kind: 'question', id: 'organisation', type: 'text', label: 'Organisation (freiwillig)', maxLength: 120 },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:titel}}', fields: [{ fieldId: 'field:Referent*in', q: 'name' }] },
      routes: [
        { id: 'r1', name: 'Vortrag', when: is('format', 'vortrag'), labels: { mode: 'add', ids: ['label:Vortrag'] } },
        { id: 'r2', name: 'Workshop', when: is('format', 'workshop'), labels: { mode: 'add', ids: ['label:Workshop'] } },
      ],
      confirmation: { text: 'Danke, dein Beitrag ist eingegangen.', expectation: 'Wir melden uns, sobald das Programm steht.' },
      tests: [
        { id: 't1', name: 'Workshop', answers: { titel: 'Roboter bauen', format: 'workshop', kurz: 'Wir bauen kleine Roboter.', dauer: '90', technik: ['beamer'], name: 'Alex Beispiel', email: 'alex@example.org' } },
      ],
    },
  },
  {
    key: 'wettbewerb',
    name: 'Anmeldung zum Wettbewerb',
    summary: 'Team, Schule, Altersstufe und betreuende Lehrkraft – ohne Schülernamen',
    needs: {
      labels: [
        { name: 'Unterstufe', color: 'yellow' },
        { name: 'Mittelstufe', color: 'orange' },
        { name: 'Oberstufe', color: 'red' },
      ],
      fields: [
        { name: 'Schule', type: 'text' },
        { name: 'Betreuende Lehrkraft', type: 'text' },
      ],
    },
    definition: {
      schemaVersion: 1,
      title: 'Team anmelden',
      intro: 'Meldet euer Team hier an. Die Anmeldung übernimmt die betreuende Lehrkraft; Namen der Schülerinnen und Schüler brauchen wir jetzt noch nicht.',
      pages: [
        {
          id: 'p1',
          title: 'Anmeldung',
          items: [
            { kind: 'question', id: 'team', type: 'text', label: 'Teamname', required: true, maxLength: 80 },
            { kind: 'question', id: 'schule', type: 'text', label: 'Schule und Ort', required: true, maxLength: 150 },
            {
              kind: 'question',
              id: 'stufe',
              type: 'choice',
              label: 'Altersstufe',
              required: true,
              display: 'radio',
              options: [
                { id: 'unter', label: 'Unterstufe (Klasse 5–7)' },
                { id: 'mittel', label: 'Mittelstufe (Klasse 8–10)' },
                { id: 'ober', label: 'Oberstufe (ab Klasse 11)' },
              ],
            },
            { kind: 'question', id: 'anzahl', type: 'number', label: 'Wie viele sind im Team?', required: true, decimals: 0, min: 1, max: 10, unit: 'Personen' },
            { kind: 'question', id: 'idee', type: 'longtext', label: 'Eure Projektidee (falls schon bekannt)', maxLength: 2000 },
            { kind: 'question', id: 'name', type: 'text', label: 'Name der betreuenden Lehrkraft', required: true, maxLength: 120 },
            { kind: 'question', id: 'email', type: 'email', label: 'E-Mail-Adresse für Rückfragen', required: true },
            { kind: 'question', id: 'bedingungen', type: 'confirm', label: 'Wir haben die Teilnahmebedingungen gelesen.', required: true },
          ],
        },
      ],
      mapping: {
        ...baseMapping,
        title: '{{q:team}}',
        fields: [
          { fieldId: 'field:Schule', q: 'schule' },
          { fieldId: 'field:Betreuende Lehrkraft', q: 'name' },
        ],
      },
      routes: [
        { id: 'r1', name: 'Unterstufe', when: is('stufe', 'unter'), labels: { mode: 'add', ids: ['label:Unterstufe'] } },
        { id: 'r2', name: 'Mittelstufe', when: is('stufe', 'mittel'), labels: { mode: 'add', ids: ['label:Mittelstufe'] } },
        { id: 'r3', name: 'Oberstufe', when: is('stufe', 'ober'), labels: { mode: 'add', ids: ['label:Oberstufe'] } },
      ],
      confirmation: { text: 'Danke, euer Team ist angemeldet.', expectation: 'Ihr bekommt die nächsten Schritte per E-Mail.' },
      tests: [
        { id: 't1', name: 'Mittelstufe', answers: { team: 'Die Tüftler', schule: 'Realschule Musterstadt', stufe: 'mittel', anzahl: 3, name: 'Frau Beispiel', email: 'lehrkraft@example.org', bedingungen: true } },
      ],
    },
  },
  {
    key: 'bestellung',
    name: 'Bestellung',
    summary: 'Was, wie viel, bis wann – Abholung oder Versand, Eilt-Kennzeichen',
    needs: {
      labels: [
        { name: 'Express', color: 'red' },
        { name: 'Abholung', color: 'blue' },
        { name: 'Versand', color: 'green' },
      ],
      fields: [
        { name: 'Kunde', type: 'text' },
        { name: 'Menge', type: 'number' },
        { name: 'Liefertermin', type: 'date' },
      ],
    },
    definition: {
      schemaVersion: 1,
      title: 'Bestellung aufgeben',
      intro: 'Sag uns, was du brauchst. Wir bestätigen die Bestellung, bevor wir loslegen.',
      pages: [
        {
          id: 'p1',
          title: 'Bestellung',
          items: [
            { kind: 'question', id: 'artikel', type: 'text', label: 'Was möchtest du bestellen?', required: true, maxLength: 150 },
            { kind: 'question', id: 'menge', type: 'number', label: 'Menge', required: true, decimals: 0, min: 1, max: 100000, unit: 'Stück' },
            { kind: 'question', id: 'details', type: 'longtext', label: 'Details (Farbe, Größe, Ausführung …)', maxLength: 3000 },
            { kind: 'question', id: 'termin', type: 'date', label: 'Wunschtermin', help: 'Den verbindlichen Liefertermin bestätigen wir dir.' },
            {
              kind: 'question',
              id: 'lieferung',
              type: 'choice',
              label: 'Abholung oder Versand?',
              required: true,
              display: 'radio',
              options: [
                { id: 'abholung', label: 'Ich hole ab' },
                { id: 'versand', label: 'Bitte schicken' },
              ],
            },
            { kind: 'question', id: 'adresse', type: 'longtext', label: 'Lieferadresse', maxLength: 500, visibleIf: is('lieferung', 'versand'), requiredIf: is('lieferung', 'versand') },
            { kind: 'question', id: 'eilt', type: 'yesno', label: 'Eilt es?' },
            { kind: 'question', id: 'kunde', type: 'text', label: 'Name oder Organisation', required: true, maxLength: 120 },
            { kind: 'question', id: 'email', type: 'email', label: 'E-Mail-Adresse für Rückfragen', required: true },
            { kind: 'question', id: 'telefon', type: 'phone', label: 'Telefon (freiwillig)' },
          ],
        },
      ],
      mapping: {
        ...baseMapping,
        title: '{{q:artikel}}',
        fields: [
          { fieldId: 'field:Kunde', q: 'kunde' },
          { fieldId: 'field:Menge', q: 'menge' },
          { fieldId: 'field:Liefertermin', q: 'termin' },
        ],
      },
      routes: [
        { id: 'r1', name: 'Eilt, Abholung', when: both(yes('eilt'), is('lieferung', 'abholung')), labels: add('Express', 'Abholung'), priority: 3 },
        { id: 'r2', name: 'Eilt, Versand', when: both(yes('eilt'), is('lieferung', 'versand')), labels: add('Express', 'Versand'), priority: 3 },
        { id: 'r3', name: 'Abholung', when: is('lieferung', 'abholung'), labels: add('Abholung') },
        { id: 'r4', name: 'Versand', when: is('lieferung', 'versand'), labels: add('Versand') },
      ],
      confirmation: { text: 'Danke, deine Bestellung ist eingegangen.', expectation: 'Wir bestätigen sie dir mit Liefertermin.' },
      tests: [
        { id: 't1', name: 'Versand', answers: { artikel: 'Vereins-T-Shirt', menge: 20, lieferung: 'versand', adresse: 'Hauptstraße 1, 12345 Musterstadt', eilt: true, kunde: 'Sportverein', email: 'alex@example.org' } },
      ],
    },
  },
  {
    key: 'druckauftrag',
    name: 'Druckauftrag',
    summary: 'Produkt, Endformat, Auflage, Papier, Liefertermin und Druckdaten',
    needs: {
      labels: [
        { name: 'Flyer', color: 'blue' },
        { name: 'Plakat', color: 'orange' },
        { name: 'Broschüre', color: 'purple' },
        { name: 'Eilauftrag', color: 'red' },
      ],
      fields: [
        { name: 'Auflage', type: 'number' },
        { name: 'Endformat', type: 'text' },
        { name: 'Material / Papier', type: 'text' },
      ],
    },
    definition: {
      schemaVersion: 1,
      title: 'Druckauftrag',
      intro: 'Was soll gedruckt werden? Druckfertige Dateien kannst du gleich mitschicken.',
      pages: [
        {
          id: 'p1',
          title: 'Auftrag',
          items: [
            { kind: 'question', id: 'titel', type: 'text', label: 'Titel', required: true, maxLength: 120, placeholder: 'z. B. Flyer Herbstprogramm' },
            {
              kind: 'question',
              id: 'produkt',
              type: 'choice',
              label: 'Produkt',
              required: true,
              display: 'radio',
              options: [
                { id: 'flyer', label: 'Flyer' },
                { id: 'plakat', label: 'Plakat' },
                { id: 'broschuere', label: 'Broschüre' },
                { id: 'sonst', label: 'Etwas anderes' },
              ],
            },
            { kind: 'question', id: 'endformat', type: 'text', label: 'Endformat', required: true, maxLength: 60, placeholder: 'z. B. A5, DIN lang, 50 × 70 cm' },
            { kind: 'question', id: 'seiten', type: 'number', label: 'Seitenzahl', decimals: 0, min: 4, max: 500, visibleIf: is('produkt', 'broschuere'), requiredIf: is('produkt', 'broschuere') },
            { kind: 'question', id: 'auflage', type: 'number', label: 'Auflage', required: true, decimals: 0, min: 1, max: 1000000, unit: 'Stück' },
            { kind: 'question', id: 'papier', type: 'text', label: 'Papier (falls bekannt)', maxLength: 100, placeholder: 'z. B. 135 g Bilderdruck matt' },
            { kind: 'question', id: 'termin', type: 'date', label: 'Liefertermin', required: true },
            { kind: 'question', id: 'adresse', type: 'longtext', label: 'Lieferadresse', maxLength: 500 },
            { kind: 'question', id: 'eilt', type: 'yesno', label: 'Ist es ein Eilauftrag?' },
            { kind: 'question', id: 'dateien', type: 'file', label: 'Druckdaten (PDF) oder Entwürfe', maxFiles: 5, maxSizeMb: 10, accept: ['pdf', 'image'] },
            ...contact,
          ],
        },
      ],
      mapping: {
        ...baseMapping,
        title: '{{q:titel}}',
        due: { q: 'termin' },
        fields: [
          { fieldId: 'field:Auflage', q: 'auflage' },
          { fieldId: 'field:Endformat', q: 'endformat' },
          { fieldId: 'field:Material / Papier', q: 'papier' },
        ],
      },
      routes: [
        { id: 'r1', name: 'Flyer, Eilauftrag', when: both(is('produkt', 'flyer'), yes('eilt')), labels: add('Flyer', 'Eilauftrag'), priority: 3 },
        { id: 'r2', name: 'Plakat, Eilauftrag', when: both(is('produkt', 'plakat'), yes('eilt')), labels: add('Plakat', 'Eilauftrag'), priority: 3 },
        { id: 'r3', name: 'Broschüre, Eilauftrag', when: both(is('produkt', 'broschuere'), yes('eilt')), labels: add('Broschüre', 'Eilauftrag'), priority: 3 },
        { id: 'r4', name: 'Eilauftrag', when: yes('eilt'), labels: add('Eilauftrag'), priority: 3 },
        { id: 'r5', name: 'Flyer', when: is('produkt', 'flyer'), labels: add('Flyer') },
        { id: 'r6', name: 'Plakat', when: is('produkt', 'plakat'), labels: add('Plakat') },
        { id: 'r7', name: 'Broschüre', when: is('produkt', 'broschuere'), labels: add('Broschüre') },
      ],
      confirmation: { text: 'Danke, dein Druckauftrag ist eingegangen.', expectation: 'Wir prüfen die Daten und melden uns vor der Druckfreigabe.' },
      tests: [
        { id: 't1', name: 'Flyer', answers: { titel: 'Flyer Herbst', produkt: 'flyer', endformat: 'A5', auflage: 1000, termin: '2030-10-01', name: 'Alex Beispiel', email: 'alex@example.org' } },
      ],
    },
  },
  {
    key: 'themenvorschlag',
    name: 'Thema oder Beitrag vorschlagen',
    summary: 'Für Redaktion und Newsletter – Thema, Inhalt, Anlass, Bilder',
    needs: { labels: [], fields: [] },
    definition: {
      schemaVersion: 1,
      title: 'Thema vorschlagen',
      intro: 'Ihr habt etwas zu berichten – eine Veranstaltung, ein Projekt, eine Neuigkeit? Schickt uns euren Vorschlag.',
      pages: [
        {
          id: 'p1',
          title: 'Dein Vorschlag',
          items: [
            { kind: 'question', id: 'thema', type: 'text', label: 'Thema', required: true, maxLength: 150 },
            { kind: 'question', id: 'inhalt', type: 'longtext', label: 'Worum geht es? (gern auch ein fertiger Text)', required: true, maxLength: 8000 },
            { kind: 'question', id: 'anlass', type: 'date', label: 'Gibt es einen Termin oder Anlass?', help: 'z. B. das Datum der Veranstaltung' },
            { kind: 'question', id: 'link', type: 'url', label: 'Weiterführender Link (freiwillig)' },
            { kind: 'question', id: 'bilder', type: 'file', label: 'Bilder', maxFiles: 5, maxSizeMb: 10, accept: ['image'], help: 'Bitte nur Bilder, an denen ihr die Rechte habt.' },
            { kind: 'question', id: 'rechte', type: 'confirm', label: 'Wir dürfen Text und Bilder veröffentlichen.', requiredIf: { mode: 'all', items: [{ q: 'bilder', op: 'answered' as const }] } },
            ...contact,
          ],
        },
      ],
      mapping: { ...baseMapping, title: '{{q:thema}}', coverQ: 'bilder' },
      routes: [],
      confirmation: { text: 'Danke für deinen Vorschlag.', expectation: 'Wir melden uns, wenn wir ihn einplanen.' },
      tests: [{ id: 't1', name: 'Ohne Bilder', answers: { thema: 'Sommerfest war toll', inhalt: '200 Gäste kamen.', name: 'Alex Beispiel', email: 'alex@example.org' } }],
    },
  },
  {
    key: 'rechnung',
    name: 'Rechnung einreichen',
    summary: 'Eingangsrechnung mit Lieferant, Betrag, Fälligkeit, Skonto und Datei',
    needs: {
      labels: [{ name: 'Dringend', color: 'red' }],
      fields: [
        { name: 'Rechnungsnr.', type: 'text' },
        { name: 'Lieferant', type: 'text' },
        { name: 'Betrag', type: 'currency' },
        { name: 'Skonto bis', type: 'date' },
        { name: 'Kostenstelle', type: 'text' },
      ],
    },
    definition: {
      schemaVersion: 1,
      title: 'Rechnung einreichen',
      intro: 'Reich hier Rechnungen ein, die bezahlt werden sollen. Bitte die Rechnung als PDF oder Foto anhängen.',
      pages: [
        {
          id: 'p1',
          title: 'Rechnung',
          items: [
            { kind: 'question', id: 'lieferant', type: 'text', label: 'Lieferant oder Firma', required: true, maxLength: 120 },
            { kind: 'question', id: 'rechnungsnr', type: 'text', label: 'Rechnungsnummer', maxLength: 60 },
            { kind: 'question', id: 'betrag', type: 'currency', label: 'Betrag (brutto)', required: true, min: 0, max: 1000000 },
            { kind: 'question', id: 'faellig', type: 'date', label: 'Zahlbar bis', help: 'Steht meist unten auf der Rechnung („zahlbar bis“ oder „Zahlungsziel“).' },
            { kind: 'question', id: 'skonto', type: 'date', label: 'Skonto bis (falls angeboten)' },
            { kind: 'question', id: 'kostenstelle', type: 'text', label: 'Wofür? (Projekt, Bereich oder Kostenstelle)', maxLength: 80 },
            { kind: 'question', id: 'datei', type: 'file', label: 'Rechnung', required: true, maxFiles: 3, maxSizeMb: 10, accept: ['pdf', 'image'] },
            { kind: 'question', id: 'hinweis', type: 'longtext', label: 'Hinweis für die Buchhaltung', maxLength: 2000 },
            { kind: 'question', id: 'eilt', type: 'yesno', label: 'Eilt die Zahlung (z. B. Mahnung, Sperre droht)?' },
            ...contact,
          ],
        },
      ],
      mapping: {
        ...baseMapping,
        title: '{{q:lieferant}}',
        due: { q: 'faellig' },
        fields: [
          { fieldId: 'field:Rechnungsnr.', q: 'rechnungsnr' },
          { fieldId: 'field:Lieferant', q: 'lieferant' },
          { fieldId: 'field:Betrag', q: 'betrag' },
          { fieldId: 'field:Skonto bis', q: 'skonto' },
          { fieldId: 'field:Kostenstelle', q: 'kostenstelle' },
        ],
      },
      routes: [{ id: 'r1', name: 'Eilt', when: yes('eilt'), labels: add('Dringend'), priority: 3 }],
      confirmation: { text: 'Danke, die Rechnung ist eingegangen.', expectation: 'Sie wird sachlich geprüft und freigegeben, bevor sie bezahlt wird.' },
      tests: [{ id: 't1', name: 'Rechnung mit Skonto', answers: { lieferant: 'Druckerei Müller', rechnungsnr: '2026-1187', betrag: 1450, skonto: '2030-03-10', eilt: false, name: 'Alex Beispiel', email: 'alex@example.org' } }],
    },
  },
  {
    key: 'auslage',
    name: 'Auslage einreichen',
    summary: 'Auslagen und Fahrtkosten erstatten lassen – mit Beleg oder Kilometern',
    needs: {
      labels: [
        { name: 'Reisekosten', color: 'blue' },
        { name: 'Bewirtung', color: 'orange' },
        { name: 'Material', color: 'green' },
      ],
      fields: [
        { name: 'Betrag', type: 'currency' },
        { name: 'Kilometer', type: 'number' },
        { name: 'Anlass', type: 'text' },
        { name: 'Kostenstelle', type: 'text' },
      ],
    },
    definition: {
      schemaVersion: 1,
      title: 'Auslage einreichen',
      intro: 'Du hast etwas vorgestreckt oder bist für uns gefahren? Reich es hier ein – mit Beleg, bei Fahrten mit den Kilometern.',
      pages: [
        {
          id: 'p1',
          title: 'Auslage',
          items: [
            { kind: 'question', id: 'anlass', type: 'text', label: 'Wofür?', required: true, maxLength: 120, placeholder: 'z. B. Material für die Kindergruppe' },
            {
              kind: 'question',
              id: 'art',
              type: 'choice',
              label: 'Art',
              required: true,
              display: 'radio',
              options: [
                { id: 'fahrt', label: 'Fahrt mit dem eigenen Auto' },
                { id: 'bewirtung', label: 'Bewirtung' },
                { id: 'material', label: 'Material oder Einkauf' },
                { id: 'sonst', label: 'Etwas anderes' },
              ],
            },
            { kind: 'question', id: 'km', type: 'number', label: 'Gefahrene Kilometer (hin und zurück)', decimals: 0, min: 1, max: 5000, unit: 'km', visibleIf: is('art', 'fahrt'), requiredIf: is('art', 'fahrt') },
            { kind: 'question', id: 'strecke', type: 'text', label: 'Strecke', maxLength: 150, placeholder: 'z. B. Musterstadt – Landeshauptstadt', visibleIf: is('art', 'fahrt') },
            { kind: 'question', id: 'betrag', type: 'currency', label: 'Betrag laut Beleg', min: 0, max: 100000, requiredIf: { mode: 'all', items: [{ q: 'art', op: 'neq', value: 'fahrt' }] } },
            { kind: 'question', id: 'beleg', type: 'file', label: 'Beleg (Foto oder PDF)', maxFiles: 5, maxSizeMb: 10, accept: ['image', 'pdf'], requiredIf: { mode: 'all', items: [{ q: 'art', op: 'neq', value: 'fahrt' }] } },
            { kind: 'question', id: 'kostenstelle', type: 'text', label: 'Projekt oder Bereich (falls bekannt)', maxLength: 80 },
            ...contact,
          ],
        },
      ],
      mapping: {
        ...baseMapping,
        title: '{{q:anlass}} – {{q:name}}',
        fields: [
          { fieldId: 'field:Betrag', q: 'betrag' },
          { fieldId: 'field:Kilometer', q: 'km' },
          { fieldId: 'field:Anlass', q: 'anlass' },
          { fieldId: 'field:Kostenstelle', q: 'kostenstelle' },
        ],
      },
      routes: [
        { id: 'r1', name: 'Fahrt', when: is('art', 'fahrt'), labels: add('Reisekosten') },
        { id: 'r2', name: 'Bewirtung', when: is('art', 'bewirtung'), labels: add('Bewirtung') },
        { id: 'r3', name: 'Material', when: is('art', 'material'), labels: add('Material') },
      ],
      confirmation: { text: 'Danke, deine Auslage ist eingegangen.', expectation: 'Nach der Freigabe überweisen wir den Betrag auf dein bekanntes Konto.' },
      tests: [
        { id: 't1', name: 'Fahrt', answers: { anlass: 'Landesverbandstreffen', art: 'fahrt', km: 148, name: 'Alex Beispiel', email: 'alex@example.org' } },
        { id: 't2', name: 'Material', answers: { anlass: 'Bastelmaterial', art: 'material', betrag: 23.5, name: 'Alex Beispiel', email: 'alex@example.org' } },
      ],
    },
  },
];

/** Leeres Formular: eine Seite, eine Frage, Titel aus der Frage */
export function blankFormDefinition(): FormTemplate['definition'] {
  return {
    schemaVersion: 1,
    title: 'Neues Formular',
    intro: '',
    pages: [{ id: 'p1', title: 'Seite 1', items: [{ kind: 'question', id: 'titel', type: 'text', label: 'Worum geht es?', required: true, maxLength: 150 }] }],
    mapping: { ...baseMapping, title: '{{q:titel}}', contactQ: null },
    routes: [],
    confirmation: { text: 'Danke, deine Angaben sind eingegangen.' },
    tests: [],
  };
}
